import { Injectable, NgZone, inject } from '@angular/core';
import { ToastController } from '@ionic/angular';
import { APP_VERSION } from '../version';

/**
 * Update checker (TalentHub365 UX, service-worker-free mechanics): every build
 * stamps APP_VERSION into the bundle and version.json into hosting; we poll
 * the json every 5 minutes and when the phone comes back to the foreground.
 * A mismatch means a newer build is deployed → "Reload / Later" toast.
 * index.html is no-cache and bundles are hash-named, so a reload IS the update.
 */
@Injectable({ providedIn: 'root' })
export class UpdateService {
  private readonly toast = inject(ToastController);
  private readonly zone = inject(NgZone);
  private promptShown = false;

  start(): void {
    void this.check();
    this.zone.runOutsideAngular(() => {
      window.setInterval(() => void this.check(), 5 * 60 * 1000);
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') void this.check();
      });
    });
  }

  currentVersion(): string {
    return APP_VERSION;
  }

  private async check(): Promise<void> {
    try {
      const res = await fetch(`/assets/version.json?t=${Date.now()}`, { cache: 'no-store' });
      if (!res.ok) return;
      const { version } = (await res.json()) as { version?: string };
      if (version && version !== APP_VERSION) {
        this.zone.run(() => void this.promptReload(version));
      }
    } catch {
      /* offline — try again next tick */
    }
  }

  private async promptReload(next: string): Promise<void> {
    if (this.promptShown) return;
    this.promptShown = true;
    const t = await this.toast.create({
      message: `A new version is available (${next}).`,
      position: 'bottom',
      duration: 0, // persist until the user chooses — never silently reload under them
      buttons: [
        { text: 'Reload', role: 'reload', handler: () => window.location.reload() },
        { text: 'Later', role: 'cancel', handler: () => (this.promptShown = false) },
      ],
    });
    await t.present();
  }
}
